import React, { useState } from 'react';
import './faqs.css'; 

function FAQs() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [activeDivision, setActiveDivision] = useState('Medical Advisory');

  const toggleAnswer = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Division 1: Medical Advisory & Applied Psychology Division
  const medicalAdvisoryQA = [
    {
      question: "1. What exactly is the Medical Advisory & Applied Psychology Division?",
      answer: "This division provides expert interpretation, behavioral insight, and strategic health guidance—helping you understand complex medical and psychological information clearly, without replacing your doctor or therapist."
    },
    {
      question: "2. How is this different from a hospital or clinic?",
      answer: "We do not treat patients or prescribe medication. Instead, we analyze, interpret, and guide decisions based on your existing medical data and life context."
    },
    {
      question: "3. Who should use this service?",
      answer: "This service is ideal for individuals who: • Feel confused about medical reports • Have multiple doctor opinions • Want a structured second opinion • Seek behavioral or lifestyle optimization"
    },
    {
      question: "4. Do you replace my doctor or specialist?",
      answer: "No. We support—not replace—your treating doctors by helping you better understand and communicate with them."
    },
    {
      question: "5. Is this service suitable for international clients?",
      answer: "Yes. Our advisory model is designed for remote and global consultations, especially for complex or second-opinion cases."
    },
    {
      question: "6. What kind of medical reports can you interpret?",
      answer: "We analyze: • Blood tests and biomarkers • MRI, CT scans, X-rays, ultrasound • Specialist reports • Hospital discharge summaries"
    },
    {
      question: "7. Can you give a second opinion on my diagnosis?",
      answer: "Yes. We provide structured second-opinion analysis, explaining different possibilities and medical reasoning."
    },
    {
      question: "8. Will you recommend treatments or medications?",
      answer: "No. We provide decision-support insights, not prescriptions."
    },
    {
      question: "9. Can you help me understand multiple doctor opinions?",
      answer: "Yes. We integrate and compare different medical opinions, helping you see the bigger picture clearly."
    },
    {
      question: "10. Do you assess risks in my condition?",
      answer: "Yes. We identify red flags, progression risks, and hidden concerns using evidence-based analysis."
    },
    {
      question: "11. Do you provide diet plans?",
      answer: "We provide clinical, personalized nutrition strategies based on your metabolism, condition, and behavior."
    },
    {
      question: "12. Can you help with diabetes or weight management?",
      answer: "Yes. We design metabolic and nutrition strategies for diabetes, obesity, and lifestyle diseases."
    },
    {
      question: "13. Do you cover hormonal and endocrine conditions?",
      answer: "Yes, including PCOS, thyroid disorders, and metabolic imbalances through nutrition and behavioral frameworks."
    },
    {
      question: "14. Is this a mental health or therapy service?",
      answer: "No. This is non-clinical applied psychology, focused on behavior, performance, and decision-making—not psychiatric treatment."
    },
    {
      question: "15. What psychological areas do you cover?",
      answer: "We work on: • Stress and emotional control • Decision-making • Habit change • Performance optimization • Life strategy"
    },
    {
      question: "16. Can you help with anxiety or stress?",
      answer: "Yes, at a non-clinical level, using structured behavioral and cognitive frameworks."
    },
    {
      question: "17. Do you provide support for children and students?",
      answer: "Yes. We offer child behavior guidance, study optimization, and developmental advisory."
    },
    {
      question: "18. How do you use AI in your services?",
      answer: "We use AI for: • Behavioral pattern analysis • Health data interpretation • Predictive insights and tracking"
    },
    {
      question: "19. Is my personal data safe?",
      answer: "Yes. We follow strict confidentiality, secure storage, and ethical data governance standards."
    },
    {
      question: "20. What services do you NOT provide?",
      answer: "We do NOT: • Prescribe medication • Diagnose psychiatric illness • Provide emergency care • Perform medical or cosmetic procedures. We ONLY provide structured advisory, interpretation, and strategic guidance."
    }
  ];

  // Division 2: Medico-Legal & Medical Ethics Division
  const medicoLegalQA = [
    {
      question: "1. What is the Medico-Legal & Medical Ethics Division?",
      answer: "It is an advanced clinical–legal intelligence system that integrates medical science, legal reasoning, and data-driven analysis to support litigation, risk management, and decision-making."
    },
    {
      question: "2. How is your service different from traditional medico-legal reports?",
      answer: "Traditional reports describe events. We provide structured analysis, legal relevance, and strategic insights that directly support case outcomes."
    },
    {
      question: "3. Who should use this service?",
      answer: "• Law firms (plaintiff & defense) • Hospitals and healthcare institutions • Insurance companies • Corporates handling injury or liability cases"
    },
    {
      question: "4. Can you assess whether a case is strong or weak?",
      answer: "Yes. We provide early-stage case intelligence, identifying viability, risks, and strategic direction before litigation begins."
    },
    {
      question: "5. Do you provide expert medico-legal reports for court?",
      answer: "Yes. Our reports are court-ready, logically structured, and designed for cross-examination."
    },
    {
      question: "6. What types of cases do you handle?",
      answer: "We support cases involving: • Medical negligence • Personal injury • Disability and impairment • Cause of death • Clinical documentation disputes"
    },
    {
      question: "7. Can you help lawyers build case strategy?",
      answer: "Yes. We work as a strategic partner, assisting with argument development, case theory, and medical reasoning."
    },
    {
      question: "8. Do you assist in cross-examination preparation?",
      answer: "Yes. We help structure medical arguments and identify weaknesses in opposing expert opinions."
    },
    {
      question: "9. Do you provide independent second opinions?",
      answer: "Yes. We offer neutral, evidence-based opinions in disputed or complex medico-legal cases."
    },
    {
      question: "10. Can you support insurance claims and fraud detection?",
      answer: "Yes. We assist with: • Claim validation • Injury verification • Fraud risk analysis • Cost justification"
    },
    {
      question: "11. Do you help hospitals reduce legal risk?",
      answer: "Yes. We provide clinical audits, documentation review, and risk advisory systems to minimize litigation exposure."
    },
    {
      question: "12. Can you analyze medical errors or incidents?",
      answer: "Yes. We conduct incident analysis and root-cause evaluation to improve safety and compliance."
    },
    {
      question: "13. How do you use data science in medico-legal services?",
      answer: "We apply: • Case outcome probability models • Compensation trend analysis • Pattern recognition across similar cases"
    },
    {
      question: "14. Are your opinions biased toward one party?",
      answer: "No. We maintain strict neutrality and ethical independence, supporting both plaintiff and defense objectively."
    },
    {
      question: "15. What is your process when handling a case?",
      answer: "Our structured model includes: 1. Case intake & confidentiality 2. Clinical and legal analysis 3. Report development 4. Quality assurance 5. Delivery with strategic support"
    }
  ];

  // Division 3: Biomedical Engineering & Sports Biomechanics Advisory
  const sportsBiomechanicsQA = [
    {
      question: "1. What is the Sports Biomechanics Advisory Division?",
      answer: "It is a scientific performance optimization service that analyzes movement, reduces injury risk, and improves athletic performance using medicine, engineering, and data analytics."
    },
    {
      question: "2. How are you different from a gym or sports academy?",
      answer: "We do not provide training or coaching. We provide data-driven analysis, technical correction, and performance strategy based on scientific evaluation."
    },
    {
      question: "3. Who can use this service?",
      answer: "• School and university athletes • Amateur and professional players • Coaches and sports institutions • Individuals focused on fitness performance"
    },
    {
      question: "4. What is biomechanical analysis?",
      answer: "It is the scientific study of how your body moves, including joint motion, force distribution, and efficiency."
    },
    {
      question: "5. How can biomechanical analysis improve performance?",
      answer: "By identifying inefficiencies and correcting technique, we help you move faster, stronger, and with less energy waste."
    },
    {
      question: "6. Can you help prevent sports injuries?",
      answer: "Yes. We assess movement patterns, joint stress, and load distribution to identify injury risks early."
    },
    {
      question: "7. Do you provide technique correction?",
      answer: "Yes. We offer sport-specific technical advisory to improve form, efficiency, and performance."
    },
    {
      question: "8. What sports do you specialize in?",
      answer: "We support multiple sports including: • Cricket • Football • Rugby • Athletics • Volleyball & Basketball"
    },
    {
      question: "9. What is your cricket biomechanics service?",
      answer: "We analyze bowling actions, spinal load, and shoulder stress to improve speed, accuracy, and injury prevention."
    },
    {
      question: "10. Do you support runners and sprinters?",
      answer: "Yes. We optimize stride mechanics, ground contact, and speed efficiency."
    },
    {
      question: "11. Can you help with recovery after injury?",
      answer: "Yes. We provide structured return-to-play guidance in collaboration with physiotherapists."
    },
    {
      question: "12. Do you include nutrition and psychology?",
      answer: "Yes. We integrate: • Sports nutrition • Mental conditioning • Performance psychology for complete athlete optimization."
    },
    {
      question: "13. How do you use AI in sports performance?",
      answer: "We use AI for: • Movement tracking • Pattern recognition • Injury prediction • Performance analytics"
    },
    {
      question: "14. Do you provide reports for teams or institutions?",
      answer: "Yes. We deliver: • Performance dashboards • Injury risk reports • Team analytics"
    },
    {
      question: "15. What results can I expect from your service?",
      answer: "• Improved performance efficiency • Reduced injury risk • Faster recovery • Longer athletic career lifespan"
    }
  ];

  // Division 4: AI Health Informatics & CSR Health Services Project Management
  const aiHealthCSRQA = [
    {
      question: "1. What is the AI Health Informatics & CSR Health Services Division?",
      answer: "It is a strategic advisory division that guides healthcare organizations in AI adoption, health data interpretation, and CSR health program design to ensure measurable impact and ethical governance."
    },
    {
      question: "2. Do you build AI systems or software?",
      answer: "No. We do not develop software or technology. We provide strategic guidance on how AI systems should be designed, used, and governed in healthcare settings."
    },
    {
      question: "3. How is this different from a health IT company?",
      answer: "Health IT companies build systems. We define how those systems should function, integrate, and deliver real clinical and organizational value."
    },
    {
      question: "4. What kind of organizations do you work with?",
      answer: "We work with: • Hospitals and healthcare institutions • Corporate CSR divisions • NGOs and international health agencies • Health-tech investors and policy groups"
    },
    {
      question: "5. How do you support AI adoption in healthcare?",
      answer: "We guide institutions in identifying where AI is truly needed, how it should be integrated, and how to avoid unsafe or ineffective use of AI systems."
    },
    {
      question: "6. Do you analyze healthcare data directly?",
      answer: "We do not process raw data. Instead, we provide strategic interpretation frameworks that help leadership understand what the data means for decisions and outcomes."
    },
    {
      question: "7. Can you help reduce risks in AI-based healthcare systems?",
      answer: "Yes. We design AI risk, ethics, and governance frameworks to reduce clinical, legal, and operational risks."
    },
    {
      question: "8. What is your approach to CSR in healthcare?",
      answer: "We design CSR as structured, measurable health impact systems—not one-time charity activities or awareness campaigns."
    },
    {
      question: "9. Can you design CSR programs for companies?",
      answer: "Yes. We create long-term CSR health strategies with defined outcomes, target populations, and impact measurement systems."
    },
    {
      question: "10. How do you measure CSR impact?",
      answer: "We design frameworks that evaluate: • Health outcomes • Behavioral changes • Community impact • Long-term sustainability of interventions"
    }
  ];

  // Division 5: Digital Health Accessories, Wellness & Sports Biomechanics Solutions
  const digitalWellnessQA = [
    {
      question: "1. What is this division about?",
      answer: "This division focuses on non-clinical wellness products, digital health accessories, and sports biomechanics tools designed to improve lifestyle, fitness, and daily performance."
    },
    {
      question: "2. Are these medical or treatment devices?",
      answer: "No. All products are strictly non-medical and non-clinical, intended for wellness, fitness, and lifestyle support only."
    },
    {
      question: "3. What types of products will you offer?",
      answer: "We offer: • Smart wearable devices • Fitness accessories • Wellness and relaxation tools • Sports biomechanics equipment"
    },
    {
      question: "4. Do your products diagnose or treat diseases?",
      answer: "No. None of our products are intended for diagnosis, treatment, or prevention of disease."
    },
    {
      question: "5. What are digital health accessories?",
      answer: "These are smart wearable and mobile-connected devices that help users track activity, movement, and lifestyle habits."
    },
    {
      question: "6. Do these devices provide medical monitoring?",
      answer: "No. They are designed for general fitness and lifestyle awareness only, not clinical monitoring."
    },
    {
      question: "7. What kind of wellness products do you provide?",
      answer: "We offer products such as: • Massage devices • Relaxation tools • Sleep-support accessories • Stress-relief gadgets"
    },
    {
      question: "8. What is the main purpose of these products?",
      answer: "They are designed to improve comfort, relaxation, and everyday wellness routines."
    },
    {
      question: "9. Can these products replace medical treatment?",
      answer: "No. They are not substituting for medical care or therapy."
    },
    {
      question: "10. What is sports biomechanics equipment?",
      answer: "These are tools that help improve movement efficiency, posture, strength, and physical performance."
    },
    {
      question: "11. Who should use these products?",
      answer: "They are ideal for: • Athletes • Fitness enthusiasts • Coaches and sports academies • Active individuals"
    },
    {
      question: "12. Do you provide rehabilitation equipment?",
      answer: "We provide basic fitness and movement support tools, not clinical rehabilitation devices."
    },
    {
      question: "13. Where do your products come from?",
      answer: "We use a global sourcing model, primarily from trusted manufacturers in China, combined with local quality and distribution control."
    },
    {
      question: "14. How do you ensure product quality?",
      answer: "We carefully select suppliers based on: • Quality standards • Reliability • Safety compliance • User experience"
    },
    {
      question: "15. Are your products legally and ethically compliant?",
      answer: "Yes. All products are: • Clearly classified as non-clinical wellness tools • Marketed responsibly • Aligned with regulatory expectations, including NMRA guidelines"
    }
  ];

  const getQuestionsByDivision = () => {
    switch(activeDivision) {
      case 'Medical Advisory':
        return medicalAdvisoryQA;
      case 'Medico-Legal':
        return medicoLegalQA;
      case 'Sports Biomechanics':
        return sportsBiomechanicsQA;
      case 'AI & CSR':
        return aiHealthCSRQA;
      case 'Digital Wellness':
        return digitalWellnessQA;
      default:
        return medicalAdvisoryQA;
    }
  };

  return (
    <div className="faq-container">
      <h1 className="faq-title">Frequently Asked Questions</h1>
      
      {/* Division Navigation Tabs */}
      <div className="faq-tabs">
        <button 
          className={`tab-btn ${activeDivision === 'Medical Advisory' ? 'active' : ''}`}
          onClick={() => { setActiveDivision('Medical Advisory'); setActiveIndex(null); }}
        >
          Medical Advisory & Psychology
        </button>
        <button 
          className={`tab-btn ${activeDivision === 'Medico-Legal' ? 'active' : ''}`}
          onClick={() => { setActiveDivision('Medico-Legal'); setActiveIndex(null); }}
        >
          Medico-Legal & Ethics
        </button>
        <button 
          className={`tab-btn ${activeDivision === 'Sports Biomechanics' ? 'active' : ''}`}
          onClick={() => { setActiveDivision('Sports Biomechanics'); setActiveIndex(null); }}
        >
          Sports Biomechanics
        </button>
        <button 
          className={`tab-btn ${activeDivision === 'AI & CSR' ? 'active' : ''}`}
          onClick={() => { setActiveDivision('AI & CSR'); setActiveIndex(null); }}
        >
          AI Health & CSR
        </button>
        <button 
          className={`tab-btn ${activeDivision === 'Digital Wellness' ? 'active' : ''}`}
          onClick={() => { setActiveDivision('Digital Wellness'); setActiveIndex(null); }}
        >
          Digital Wellness
        </button>
      </div>

      {/* FAQ List for Selected Division */}
      <div className="faq-list">
        {getQuestionsByDivision().map((item, index) => (
          <div key={index} className="faq-item">
            <div className="faq-question" onClick={() => toggleAnswer(index)}>
              <h3>{item.question}</h3>
              <span className={`toggle-icon ${activeIndex === index ? 'active' : ''}`}>&#9660;</span>
            </div>
            {activeIndex === index && <div className="faq-answer"><p>{item.answer}</p></div>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default FAQs;